import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
/* The lock lives in `utils/bodyScrollLock` rather than here: eleven other
   components used to write `body.style.overflow` themselves, six of them
   while ALSO rendering one of these — so they double-locked and the count
   that makes stacking work only means something if everyone shares it. */
import {
  lockBodyScroll,
  releaseBodyScroll,
} from "../../utils/bodyScrollLock";
import { useTourModal } from "../../tours/tourModals";

/**
 * BdrModal — the app's modal shell.
 *
 * The CSS lives in the global `index.css` (`.bdr-modal-*`, registry §26.1).
 * This component exists so the three things every modal has to get right are
 * gotten right ONCE instead of 25 times:
 *
 *   1. PORTAL to <body>. Rendered inline, a modal is at the mercy of any
 *      ancestor's `overflow` or stacking context. Most of the app's modals
 *      work today only incidentally.
 *   2. LAYER from the overlay ladder, never a typed z-index. Portalling alone
 *      does NOT order siblings — two body-portalled overlays still stack by
 *      z-index, which is how ImageModal (MUI default 1300) ended up rendering
 *      behind the calendar's event-detail overlay (1400) in 2026-06-23.
 *   3. BODY SCROLL LOCK that is reference-counted. A naive lock/unlock pair
 *      breaks with stacked modals: closing the inner one unlocks scrolling
 *      while the outer one is still open.
 *
 * Structure is slot-based so a caller keeps full control of its own content:
 *
 *   <BdrModal open={open} onClose={close} title="..." actions={<>…</>}>
 *     …body…
 *   </BdrModal>
 *
 * Surface / width overrides: pass `surface` or `maxWidth` with a TOKEN
 * (`"var(--bdr-grad-panel)"`, `"1100px"`). These set the local CSS variables
 * the panel reads, so no new class is needed for a one-off.
 */

const LAYER_CLASS = {
  base: "",
  raised: "bdr-modal-overlay--raised",
  top: "bdr-modal-overlay--top",
  peak: "bdr-modal-overlay--peak",
  // Not part of the four-deep modal stack: an unprompted, app-level
  // interruption that outranks even a confirm because it arrives on a timer.
  // The clock-in reminder is the only user.
  nag: "bdr-modal-overlay--nag",
  // Also not a rung: a dropdown list that has become a sheet on a narrow
  // screen. The field it belongs to can sit inside a modal at any rung, so it
  // takes the popover level the anchored list already uses rather than
  // guessing one rung above its host. BdrSelectPopup is the only user.
  popover: "bdr-modal-overlay--popover",
};

const BdrModal = ({
  open,
  onClose,
  title,
  children,
  actions,
  /** "sm" | "md" (default) | "lg" | "full" */
  size = "md",
  /** "" (default gradient) | "panel" | "solid" — or pass `surface` for a token */
  variant = "",
  /** CSS value for the panel background, e.g. "var(--bdr-grad-panel)" */
  surface,
  /** CSS value for the panel max-width, e.g. "1100px" */
  maxWidth,
  /** "base" (default) | "raised" | "top" | "peak" — see the ladder in :root */
  layer = "base",
  /**
   * "center" (default) | "bottom".
   *
   * `bottom` pins the panel to the bottom edge — a sheet. It is for a picker
   * on a narrow screen: the bottom of the screen is where the thumb already
   * is, and the field stays visible above it so the choice keeps its context.
   * Horizontal centring and `maxWidth` still apply, so a sheet on a tablet is
   * a centred strip rather than a band across the whole width.
   */
  align = "center",
  closeOnBackdrop = true,
  closeOnEsc = true,
  /** extra classes on the panel, for a caller's own layout needs */
  panelClassName = "",
  /**
   * Extra classes on the OVERLAY.
   *
   * Rarely needed, and there is one real reason for it: printing. The shell
   * portals to <body>, so a print rule that hides `#root` does NOT hide an
   * open modal — it would print the modal's chrome over whatever the page was
   * trying to print. A caller that prints from inside itself needs a handle on
   * the overlay to hide it. Do not reach for this to restyle the backdrop.
   */
  overlayClassName = "",
  ariaLabel,
  /** set false to render a modal with no header bar at all */
  showHeader = true,
  /**
   * set false to keep the header but drop its × — for a modal the user must
   * complete (e.g. the first-login info form). Set `closeOnBackdrop` and
   * `closeOnEsc` false alongside it: hiding only the × leaves two other ways
   * out and makes the modal look mandatory without being mandatory.
   */
  showClose = true,
  /**
   * Lets a feature walkthrough close this modal (tours/tourModals.js): while
   * open, `onClose` is registered under this tag, and a tour step that names
   * the tag keeps it open; every other step and the end of the tour close it.
   * A tag is a promise that `onClose` ONLY closes — if it saves, confirms or
   * navigates, don't tag the shell; call `useTourModal(tag, open, pureClose)`
   * in the caller instead. Untagged (every modal by default): no effect.
   */
  tourTag,
}) => {
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  const returnFocusRef = useRef(null);

  useTourModal(tourTag, open, onClose);

  // Escape closes, and Tab is trapped inside the panel.
  //
  // The trap is not decoration: MUI's Dialog traps focus, so every modal
  // migrated off Dialog onto this shell would otherwise REGRESS — tabbing
  // would walk out of the modal and into the page behind it, which for a
  // keyboard or screen-reader user means silently losing the modal.
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape" && closeOnEsc) {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        // Nothing focusable inside: keep focus on the panel rather than
        // letting Tab escape to the page behind.
        event.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Wrap at both ends. Also catches focus sitting outside the panel
      // entirely (e.g. it was never moved in), pulling it back.
      if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [closeOnEsc, onClose]
  );

  // The live handler, read through a ref by a listener that is bound ONCE.
  //
  // This indirection is load-bearing. `handleKeyDown` depends on `onClose`,
  // and every caller writes that as an inline arrow (`const handleClose =
  // () => {...}`), so it is a new function on every parent render. With
  // `handleKeyDown` in the effect's dep array, the effect tore down and
  // re-ran on EVERY KEYSTROKE — re-running `panelRef.current.focus()` and
  // pulling focus out of whatever field was being typed in, as well as
  // churning the scroll lock and rebinding the listener each time.
  const keyHandlerRef = useRef(handleKeyDown);
  keyHandlerRef.current = handleKeyDown;

  useEffect(() => {
    if (!open) return undefined;

    // Remember where focus was so it can go back when the modal closes —
    // otherwise focus resets to <body> and a keyboard user restarts at the
    // top of the page.
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    lockBodyScroll();

    const onKeyDown = (event) => keyHandlerRef.current?.(event);
    document.addEventListener("keydown", onKeyDown);

    // Move focus into the modal ONCE, on open. The panel itself (tabIndex -1)
    // rather than the first field, so a screen reader announces the dialog
    // before its content, and so we never steal a text field's caret.
    const raf = requestAnimationFrame(() => panelRef.current?.focus());

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKeyDown);
      releaseBodyScroll();
      returnFocusRef.current?.focus?.();
    };
    // `open` ONLY — see the note above the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const panelClass = [
    "bdr-modal-panel",
    size && size !== "md" ? `bdr-modal-panel--${size}` : "",
    variant ? `bdr-modal-panel--${variant}` : "",
    panelClassName,
  ]
    .filter(Boolean)
    .join(" ");

  // Only set a variable when the caller actually passed one, so the class
  // modifiers keep working when they didn't.
  const panelStyle = {};
  if (surface) panelStyle["--bdr-modal-surface"] = surface;
  if (maxWidth) panelStyle["--bdr-modal-max-w"] = maxWidth;

  return createPortal(
    <div
      ref={overlayRef}
      className={[
        "bdr-modal-overlay",
        LAYER_CLASS[layer] || "",
        align === "bottom" ? "bdr-modal-overlay--bottom" : "",
        overlayClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={(event) => {
        // mousedown, not click: a text selection that STARTS inside the panel
        // and ends on the backdrop fires a click on the backdrop and would
        // close the modal mid-drag.
        if (!closeOnBackdrop) return;
        /*
         * Which overlay does this target actually belong to?
         *
         * A modal opened FROM this one portals its own overlay to <body>, but
         * React bubbles events through the REACT tree, not the DOM tree — so a
         * mousedown inside the CHILD's panel arrives here with a target that is
         * nowhere near our panel, and the check below would read it as a
         * backdrop click and close us out from under the child.
         *
         * `closest` walks the real DOM, so it answers the question React can't:
         * anything belonging to another overlay (or to a portalled popup, which
         * belongs to none) is not ours to act on. Do NOT "simplify" this to
         * stopPropagation — React's stopPropagation also stops the NATIVE
         * event, which would break document-level outside-click handlers such
         * as BdrSelectPopup's.
         */
        if (event.target?.closest?.(".bdr-modal-overlay") !== overlayRef.current)
          return;
        if (panelRef.current && panelRef.current.contains(event.target)) return;
        onClose?.();
      }}
      /*
       * Stop clicks escaping to the host. A React portal still bubbles events
       * through the REACT tree, not the DOM tree — so without this, a click
       * inside the modal reaches whatever component rendered <BdrModal>. That
       * is a live hazard here: several hosts (ViewClientQuoteModal's root div,
       * calendar event blocks) close or navigate on any click, so a click on a
       * field inside the modal would tear down the host and this modal with it.
       */
      onClick={(event) => event.stopPropagation()}
    >
      <div
        ref={panelRef}
        className={panelClass}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        /* -1 so the panel can receive focus programmatically on open without
           joining the tab order itself. */
        tabIndex={-1}
        aria-label={ariaLabel || (typeof title === "string" ? title : undefined)}
      >
        {showHeader && (
          <div className="bdr-modal-header">
            <h3 className="bdr-modal-title">{title}</h3>
            {showClose && (
              <button
                type="button"
                className="bdr-modal-close"
                aria-label="Close"
                onClick={onClose}
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className="bdr-modal-body">{children}</div>

        {actions && <div className="bdr-modal-actions">{actions}</div>}
      </div>
    </div>,
    document.body
  );
};

export default BdrModal;
